import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Pagination, Badge, Alert } from 'react-bootstrap';
import { getUserActivity, getUserActivityByName, getResourceActivity } from '../services/api/auditApi';
import { useAuth } from '../contexts/AuthContext';
import { usePageBreadcrumbs } from '../hooks/usePageBreadcrumbs';
import { resolveAdminPath } from '../utils/adminBreadcrumbUtils';

const AuditHistory = () => {
  const { hasPermission } = useAuth();
  const adminPath = resolveAdminPath(hasPermission);
  const adminSegmentPath = (adminPath && adminPath !== '/audit') ? adminPath : undefined;

  usePageBreadcrumbs([
    { label: 'Admin', path: adminSegmentPath },
    { label: 'Audit History' }
  ]);
  const [viewType, setViewType] = useState('user');
  const [searchType, setSearchType] = useState('name');
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [resourceType, setResourceType] = useState('User');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [auditEvents, setAuditEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, totalCount: 0 });

  const resourceTypes = ['User', 'Role', 'ExtractionJob'];

  const handleSearch = async (page = 1) => {
    setLoading(true);
    setError('');
    
    try {
      let response;
      if (viewType === 'user') {
        if (searchType === 'name') {
          if (!userName) {
            setError('User name is required');
            setLoading(false);
            return;
          }
          if (userName.trim().length < 2) {
            setError('User name must be at least 2 characters long');
            setLoading(false);
            return;
          }
          response = await getUserActivityByName(userName, startDate, endDate, page, pagination.pageSize);
        } else {
          if (!userId) {
            setError('User ID is required');
            setLoading(false);
            return;
          }
          response = await getUserActivity(userId, startDate, endDate, page, pagination.pageSize);
        }
      } else {
        if (!resourceId) {
          setError('Resource ID is required');
          setLoading(false);
          return;
        }
        response = await getResourceActivity(resourceId, resourceType, startDate, endDate, page, pagination.pageSize);
      }
      
      setAuditEvents(response.items || []);
      setPagination({
        page: response.page,
        pageSize: response.pageSize,
        totalCount: response.totalCount
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch audit history');
      setAuditEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getActionBadge = (action) => {
    const variants = {
      Create: 'success',
      Update: 'primary',
      Delete: 'danger',
      View: 'info',
      Export: 'warning'
    };
    return <Badge bg={variants[action] || 'secondary'}>{action}</Badge>;
  };

  const totalPages = Math.ceil(pagination.totalCount / pagination.pageSize);

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>Audit History</h2>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Form onSubmit={(e) => { e.preventDefault(); handleSearch(1); }}>
                <Row className="mb-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>View Type</Form.Label>
                      <Form.Select value={viewType} onChange={(e) => setViewType(e.target.value)}>
                        <option value="user">User Activity</option>
                        <option value="resource">Resource Activity</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {viewType === 'user' ? (
                    <>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Search By</Form.Label>
                          <Form.Select value={searchType} onChange={(e) => setSearchType(e.target.value)}>
                            <option value="name">User Name</option>
                            <option value="id">User ID</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>{searchType === 'name' ? 'User Name' : 'User ID'}</Form.Label>
                          {searchType === 'name' ? (
                            <Form.Control
                              type="text"
                              placeholder="Enter user name"
                              value={userName}
                              onChange={(e) => setUserName(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
                            />
                          ) : (
                            <Form.Control
                              type="text"
                              placeholder="Enter user ID"
                              value={userId}
                              onChange={(e) => setUserId(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
                            />
                          )}
                        </Form.Group>
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Resource Type</Form.Label>
                          <Form.Select value={resourceType} onChange={(e) => setResourceType(e.target.value)}>
                            {resourceTypes.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Resource ID</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter resource ID"
                            value={resourceId}
                            onChange={(e) => setResourceId(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
                          />
                        </Form.Group>
                      </Col>
                    </>
                  )}

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2} className="d-flex align-items-end">
                    <Button variant="primary" onClick={() => handleSearch(1)} disabled={loading}>
                      {loading ? 'Searching...' : 'Search'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card>
            <Card.Body>
              {auditEvents.length > 0 ? (
                <>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Action</th>
                        <th>Resource Type</th>
                        <th>Resource ID</th>
                        <th>User</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditEvents.map((event) => (
                        <tr key={event.id}>
                          <td>{formatDate(event.timestamp)}</td>
                          <td>{getActionBadge(event.action)}</td>
                          <td>{event.resourceType}</td>
                          <td><code>{event.resourceId}</code></td>
                          <td>{event.userName}</td>
                          <td>{event.details || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
                        {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of{' '}
                        {pagination.totalCount} events
                      </div>
                      <Pagination>
                        <Pagination.First onClick={() => handleSearch(1)} disabled={pagination.page === 1} />
                        <Pagination.Prev onClick={() => handleSearch(pagination.page - 1)} disabled={pagination.page === 1} />
                        
                        {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                          const pageNum = pagination.page - 2 + idx;
                          if (pageNum > 0 && pageNum <= totalPages) {
                            return (
                              <Pagination.Item
                                key={pageNum}
                                active={pageNum === pagination.page}
                                onClick={() => handleSearch(pageNum)}
                              >
                                {pageNum}
                              </Pagination.Item>
                            );
                          }
                          return null;
                        })}
                        
                        <Pagination.Next onClick={() => handleSearch(pagination.page + 1)} disabled={pagination.page === totalPages} />
                        <Pagination.Last onClick={() => handleSearch(totalPages)} disabled={pagination.page === totalPages} />
                      </Pagination>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  {loading ? 'Loading...' : 'No audit events found. Use the search form above to find audit history.'}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AuditHistory;
